import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { AuthCore } from '../../core/AuthCore';
import { apiService } from '../../services/apiService';
import { RefreshCw, FileText, RotateCcw } from 'lucide-react';

interface RecentCreditActivity {
  id: string;
  type: 'album_creation' | 'regen_metadata' | 'api_call';
  title: string;
  description: string;
  creditCost: number;
  albumId?: number;
  albumTitle?: string;
  albumSlug?: string;
  fileName?: string;
  assetCount?: number;
  timestamp: string;
}

const RecentCreditHistorySection: React.FC = () => {
  const [activities, setActivities] = useState<RecentCreditActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRecentHistory();
  }, []);

  const fetchRecentHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = await AuthCore.getInstance().getAccessToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app'}/credits/recent-history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch recent history: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setActivities(data.data.activities);
      } else {
        throw new Error(data.error || 'Failed to load recent history');
      }
    } catch (err) {
      console.error('❌ Failed to fetch recent credit history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load recent history');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'album_creation':
        return <FileText className="w-4 h-4 text-blue-600" />;
      case 'regen_metadata':
        return <RotateCcw className="w-4 h-4 text-orange-600" />;
      case 'api_call':
        return <RefreshCw className="w-4 h-4 text-green-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'album_creation':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'regen_metadata':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'api_call':
        return 'bg-green-50 border-green-200 text-green-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-gray-900">Recent Credit History</CardTitle>
          <p className="text-sm text-gray-600">Your recent credit usage and activities</p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading recent history...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-gray-900">Recent Credit History</CardTitle>
          <p className="text-sm text-gray-600">Your recent credit usage and activities</p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-red-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-red-600 mb-2">Failed to load recent history</p>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchRecentHistory}
              className="text-sm"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200 shadow-sm">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl font-semibold text-gray-900">Recent Credit History</CardTitle>
            <p className="text-sm text-gray-600">Your recent credit usage and activities</p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchRecentHistory}
            className="text-sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-2">No recent activity</p>
            <p className="text-sm text-gray-500">Your credit usage will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div 
                key={activity.id} 
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {activity.albumSlug ? (
                        <a 
                          href={`/results-v2/${activity.albumSlug}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {activity.title}
                        </a>
                      ) : (
                        activity.title
                      )}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatDate(activity.timestamp)}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActivityColor(activity.type)}`}>
                    {activity.creditCost} credit{activity.creditCost > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View All Activity Link */}
        {activities.length > 0 && (
          <div className="mt-6 text-center border-t border-gray-200 pt-4">
            <Button 
              variant="outline" 
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => window.location.href = '/all-activity'}
            >
              View All Activity →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentCreditHistorySection;
